(function() {
  if (customElements.get('lp-collection-filter')) return;

  function debounce(fn, wait) {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class LpCollectionFilter extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.section;
      this.debouncedApply = debounce(() => this.applyFilters(), 600);
    }

    connectedCallback() {
      this.addEventListener('change', this.onChangeHandler.bind(this));
      this.addEventListener('click', this.onClickHandler.bind(this));

      this.querySelector('[data-lp-open-filters]')?.addEventListener('click', () => this.openDrawer());
      this.querySelector('[data-lp-close-filters]')?.addEventListener('click', () => this.closeDrawer());
      this.querySelector('[data-lp-filter-overlay]')?.addEventListener('click', () => this.closeDrawer());

      const sortToggle = this.querySelector('[data-lp-sort-toggle]');
      if (sortToggle) {
        sortToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          const dropdown = this.querySelector('[data-lp-sort-dropdown]');
          dropdown.classList.toggle('is-open');
        });
        document.addEventListener('click', (e) => {
          const dropdown = this.querySelector('[data-lp-sort-dropdown]');
          if (dropdown && !dropdown.contains(e.target)) {
            dropdown.classList.remove('is-open');
          }
        });
      }

      this.initNav();
      this.initCardCarousels();
      window.addEventListener('resize', debounce(() => this.initCardCarousels(), 250));
    }

    initCardCarousels() {
      if (window.innerWidth >= 1024) return;
      if (typeof Swiper === 'undefined') {
        clearTimeout(this._carouselRetry);
        this._carouselRetry = setTimeout(() => this.initCardCarousels(), 100);
        return;
      }
      this.querySelectorAll('[data-lp-card-carousel]:not(.swiper-initialized)').forEach(el => {
        const card = el.closest('.lp-product-card');
        const link = card ? card.querySelector('.lp-product-card__link') : null;

        const swiper = new Swiper(el, {
          slidesPerView: 1,
          loop: true,
          pagination: {
            el: el.querySelector('.swiper-pagination'),
            clickable: true
          }
        });

        if (link) {
          swiper.on('tap', () => {
            window.location.href = link.href;
          });
        }
      });
    }

    initNav() {
      const nav = this.querySelector('[data-lp-nav]');
      if (!nav) return;

      const links = nav.querySelector('[data-lp-nav-links]');
      const btns = nav.querySelectorAll('[data-lp-nav-btn]');
      if (!links || !btns.length) return;

      const scrollContainer = links.parentNode;

      function updateButtons() {
        const scrollLeft = scrollContainer.scrollLeft;
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.offsetWidth;

        btns.forEach(btn => {
          if (btn.classList.contains('next')) {
            btn.classList.toggle('hidden', maxScroll <= 0 || scrollLeft >= maxScroll - 1);
          } else {
            btn.classList.toggle('hidden', scrollLeft <= 1);
          }
        });
      }

      function scrollNav(e) {
        const currentIndex = Number(links.getAttribute('data-link') || 0);
        const isNext = e.currentTarget.classList.contains('next');
        const newIndex = isNext ? currentIndex + 1 : currentIndex - 1;
        const targetLink = links.children[newIndex];
        if (!targetLink) return;

        links.setAttribute('data-link', newIndex);
        const scrollOffset = isNext ? targetLink.offsetLeft : Math.max(0, targetLink.offsetLeft - 20);
        scrollContainer.scroll({ left: scrollOffset, behavior: 'smooth' });

        setTimeout(updateButtons, 350);
      }

      updateButtons();
      scrollContainer.addEventListener('scroll', debounce(updateButtons, 100));
      window.addEventListener('resize', updateButtons);
      btns.forEach(btn => btn.addEventListener('click', scrollNav));
    }

    onChangeHandler(event) {
      if (!event.target.matches('[data-lp-filter-input]')) return;
      this.debouncedApply();
    }

    onClickHandler(event) {
      const removeBtn = event.target.closest('[data-lp-remove-filter-url]');
      if (removeBtn) {
        event.preventDefault();
        const url = removeBtn.dataset.lpRemoveFilterUrl;
        const searchParams = url.includes('?') ? url.split('?')[1] : '';
        this.fetchAndUpdate(searchParams);
      }
    }

    openDrawer() {
      const drawer = this.querySelector('[data-lp-filter-drawer]');
      const overlay = this.querySelector('[data-lp-filter-overlay]');
      if (drawer) drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    closeDrawer() {
      const drawer = this.querySelector('[data-lp-filter-drawer]');
      const overlay = this.querySelector('[data-lp-filter-overlay]');
      if (drawer) drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    applyFilters() {
      const form = this.querySelector(`#lp-filters-form-${this.sectionId}`);
      if (!form) return;
      const formData = new FormData(form);
      const searchParams = new URLSearchParams(formData);

      const priceMin = searchParams.get('filter.v.price.gte');
      const priceMax = searchParams.get('filter.v.price.lte');
      const maxRange = form.querySelector('.max-range');
      const actualMax = maxRange ? maxRange.getAttribute('max') : null;
      if ((!priceMin || priceMin === '0') && (!priceMax || priceMax === actualMax)) {
        searchParams.delete('filter.v.price.gte');
        searchParams.delete('filter.v.price.lte');
      }

      this.fetchAndUpdate(searchParams.toString());
    }

    showLoading() {
      const overlay = this.querySelector('[data-lp-loading]');
      const resultsCount = this.querySelector(`#lp-results-count-${this.sectionId}`);
      const mobileCount = this.querySelector(`#lp-results-count-mobile-${this.sectionId}`);
      if (overlay) overlay.classList.add('is-visible');
      if (resultsCount) resultsCount.classList.add('is-loading');
      if (mobileCount) mobileCount.classList.add('is-loading');
    }

    hideLoading() {
      const overlay = this.querySelector('[data-lp-loading]');
      const resultsCount = this.querySelector(`#lp-results-count-${this.sectionId}`);
      const mobileCount = this.querySelector(`#lp-results-count-mobile-${this.sectionId}`);
      if (overlay) overlay.classList.remove('is-visible');
      if (resultsCount) resultsCount.classList.remove('is-loading');
      if (mobileCount) mobileCount.classList.remove('is-loading');
    }

    fetchAndUpdate(searchParams) {
      this.showLoading();

      fetch(`${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          this.swapInner(doc, `lp-product-grid-${this.sectionId}`);
          this.swapInner(doc, `lp-results-count-${this.sectionId}`);
          this.swapInner(doc, `lp-results-count-mobile-${this.sectionId}`);
          this.swapInner(doc, `lp-active-filters-${this.sectionId}`);
          this.swapInner(doc, `lp-sort-options-${this.sectionId}`);

          const newFilters = doc.querySelectorAll('.lp-filter-section');
          newFilters.forEach(el => {
            const existing = this.querySelector(`[id="${el.id}"]`);
            if (!existing) return;
            const wasOpen = existing.querySelector('details[open]') !== null;
            existing.innerHTML = el.innerHTML;
            if (wasOpen) {
              const details = existing.querySelector('details');
              if (details) details.setAttribute('open', '');
            }
          });

          const newClear = doc.querySelector('.lp-filter-drawer__clear');
          const oldClear = this.querySelector('.lp-filter-drawer__clear');
          if (newClear && oldClear) {
            oldClear.outerHTML = newClear.outerHTML;
          } else if (!newClear && oldClear) {
            oldClear.remove();
          } else if (newClear && !oldClear) {
            const form = this.querySelector(`#lp-filters-form-${this.sectionId}`);
            if (form) form.insertAdjacentHTML('beforeend', newClear.outerHTML);
          }

          history.pushState({}, '', `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`);
          this.hideLoading();

          const sortDropdown = this.querySelector('[data-lp-sort-dropdown]');
          if (sortDropdown) sortDropdown.classList.remove('is-open');

          this.scrollToTop();
          this.initCardCarousels();

          const drawer = this.querySelector('.lp-filter-drawer');
          if (drawer) drawer.scrollTop = 0;
        })
        .catch(err => {
          console.error('Filter error:', err);
          this.hideLoading();
        });
    }

    swapInner(doc, id) {
      const src = doc.getElementById(id);
      const dest = this.querySelector(`#${id}`);
      if (src && dest) dest.innerHTML = src.innerHTML;
    }

    scrollToTop() {
      const section = this.querySelector('.lp-new-main-collection') || this;
      const header = document.querySelector('#main-header');
      const offset = header ? header.offsetHeight + 10 : 80;
      const top = section.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  customElements.define('lp-collection-filter', LpCollectionFilter);
})();
